import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { networkManager } from './networkManager';

interface OfflineContextType {
  isOnline: boolean;
  verifyConnectivity: () => Promise<boolean>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(networkManager.isOnline());

  useEffect(() => {
    // Rely on network manager subscriptions to prevent race conditions or duplicate bindings
    const unsubscribe = networkManager.subscribe((online) => {
      setIsOnline(online);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const verifyConnectivity = useCallback(async () => {
    return await networkManager.verifyRealConnectivity();
  }, []);

  const contextValue = useMemo(() => ({
    isOnline,
    verifyConnectivity,
  }), [isOnline, verifyConnectivity]);

  return (
    <OfflineContext.Provider value={contextValue}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOfflineStatus = () => {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error('useOfflineStatus must be used within an OfflineProvider');
  }
  return context;
};

export default OfflineProvider;
